import api from "./api";
import { resolveRoleName } from "../utils/auth";

// Normalizer supports multiple API shapes (snake_case and camelCase)
const normalizeRoom = (dto) => ({
  room_id: dto?.room_id ?? dto?.id,
  room_code: dto?.room_code ?? dto?.roomCode ?? dto?.code ?? "",
  name: dto?.name ?? "",
  floor_id: dto?.floor_id ?? dto?.floorId ?? null,
  floor_name:
    dto?.floor_name ?? dto?.floor?.name ?? dto?.floor ?? dto?.floorName ?? "",
  row: dto?.row ?? dto?.cell_row ?? null,
  column: dto?.column ?? dto?.cell_column ?? null,
  cellType: dto?.cellType ?? dto?.cell_type ?? "",
  space_type_id: dto?.space_type_id ?? dto?.spaceTypeId ?? dto?.space_type?.space_type_id ?? null,
  room_template_id: dto?.room_template_id ?? dto?.roomTemplateId ?? null,
  created_at: dto?.created_at ?? dto?.createdAt ?? null,
  updated_at: dto?.updated_at ?? dto?.updatedAt ?? null,
});

const extractRoomArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

export async function getRoomsPaginated({
  page = 1,
  limit = 10,
  search = "",
} = {}) {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim())
    query.append("search", String(search).trim());

  const qs = query.toString();
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN" ? "/room" : (
      "/hierarchical-view/relation/room/bulk"
    );
  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);

  const data = extractRoomArray(res);
  const meta =
    res?.meta && typeof res.meta === "object" ?
      res.meta
    : {
        total: data.length,
        page: Number(page) || 1,
        limit: Number(limit) || 10,
        totalPages: 1,
      };

  return {
    data: data.map(normalizeRoom),
    meta: {
      total: Number(meta.total) || 0,
      page: Number(meta.page) || 1,
      limit: Number(meta.limit) || Number(limit) || 10,
      totalPages: Number(meta.totalPages) || 1,
    },
  };
}

export async function patchRoom(room_id, payload) {
  const roomCode = (payload?.roomCode ?? payload?.room_code ?? "")
    .toString()
    .trim();
  const name = (payload?.name ?? "").toString().trim();
  const body = { roomCode, name };
  const data = await api.patch(`/room/${room_id}`, body);
  return normalizeRoom(data ?? { ...body, id: room_id });
}

/**
 * Update only the layout fields of a room.
 *
 * Deliberately separate from patchRoom, which sends nothing but roomCode and
 * name: routing a layout save through it would silently drop cellType and the
 * space type, and blank the room's name and code as a bonus. This one sends
 * exactly the two fields the designer owns.
 */
export async function patchRoomLayout(room_id, { cellType, space_type_id, room_template_id }) {
  const body = {};
  if (cellType) body.cellType = String(cellType).toUpperCase();
  // null is meaningful — it clears the space type — so only `undefined` skips.
  if (space_type_id !== undefined) body.space_type_id = space_type_id;
  // Null clears the pin, so an explicit null must be sent. Testing falsiness
  // here would make "Automatic" unselectable once a layout had been pinned.
  if (room_template_id !== undefined) body.room_template_id = room_template_id;
  const data = await api.patch(`/room/${room_id}`, body);
  return normalizeRoom(data ?? { id: room_id, ...body });
}

export async function createRoom(payload) {
  const body = {
    roomCode: String(payload?.roomCode ?? payload?.room_code ?? "").trim(),
    name: String(payload?.name ?? "").trim(),
    floor_id: Number(payload?.floor_id ?? payload?.floorId),
    row: Number(payload?.row ?? payload?.cell_row) || null,
    column: Number(payload?.column ?? payload?.cell_column) || null,
  };
  const cellType = String(payload?.cellType ?? payload?.cell_type ?? "").trim();
  if (cellType) body.cellType = cellType.toUpperCase();

  const data = await api.post("/room", body);
  return normalizeRoom(data ?? { ...body, id: Math.floor(Math.random() * 1000000) });
}

export { normalizeRoom };
